import {
  Activity,
  BarChart3,
  Bot,
  CheckCircle2,
  Cloud,
  Database,
  Film,
  Gauge,
  Globe2,
  LayoutDashboard,
  ListChecks,
  Mail,
  Megaphone,
  MessageSquareText,
  MousePointerClick,
  Plus,
  RefreshCw,
  Rocket,
  Search,
  ShieldCheck,
  Target,
  Wand2,
  Workflow
} from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  createOpportunity,
  createGrowthAction,
  createSite,
  createTask,
  loadCloudflareOverview,
  loadDashboard,
  loadGrowthOverview,
  loadHotGuidesAutomation,
  loadVideoGrowth,
  runCloudflareAutomation
} from "./api";
import { fallbackDashboard, fallbackGrowthOverview, fallbackVideoGrowth } from "./mockData";
import { HotGuidesPage } from "./HotGuidesPage";
import { PricingLabPage } from "./PricingLabPage";
import type {
  CloudflareEndpoint,
  CloudflareOverview,
  DashboardData,
  GrowthAction,
  GrowthOverview,
  HotGuidesAutomationOverview,
  Opportunity,
  Site,
  Task,
  VideoGrowthOverview
} from "./types";
import { VideoFactoryPage } from "./VideoFactoryPage";

const navItems = [
  { id: "dashboard", label: "控制台", icon: LayoutDashboard },
  { id: "opportunities", label: "机会池", icon: Search },
  { id: "sites", label: "站点管理", icon: Globe2 },
  { id: "factory", label: "页面工厂", icon: Workflow },
  { id: "videoGrowth", label: "视频增长", icon: BarChart3 },
  { id: "growth", label: "增长引流", icon: Megaphone },
  { id: "hotGuidesAutomation", label: "攻略自动化", icon: RefreshCw },
  { id: "tasks", label: "执行任务", icon: ListChecks },
  { id: "cloudflareApi", label: "CF API 自动化", icon: Cloud },
  { id: "stack", label: "Cloudflare 栈", icon: Cloud }
];

const growthChannels = [
  {
    channel: "Indie Hackers",
    decision: "立即执行",
    role: "Founder feedback",
    cadence: "首周 1 个反馈帖 + 每天 3 条高质量回复",
    url: "https://tools.kakijio.com/pricing-lab-tools/?utm_source=indiehackers&utm_medium=community&utm_campaign=pricing_lab_launch",
    guardrail: "透明说明是自己做的工具；只在和 SaaS 定价相关的讨论中放链接。"
  },
  {
    channel: "Reddit",
    decision: "谨慎执行",
    role: "Objection discovery",
    cadence: "每天先回复 5 条，再考虑 1 条带链接的反馈请求",
    url: "https://tools.kakijio.com/pricing-lab-tools/?utm_source=reddit&utm_medium=community&utm_campaign=pricing_lab_launch",
    guardrail: "先读每个 subreddit 规则；不跨社区复制同一段文案。"
  },
  {
    channel: "LinkedIn",
    decision: "立即执行",
    role: "B2B trust",
    cadence: "每周 2 条定价拆解帖 + 10 条评论",
    url: "https://tools.kakijio.com/pricing-lab-tools/?utm_source=linkedin&utm_medium=social&utm_campaign=pricing_lab_launch",
    guardrail: "不要承诺自动给出正确价格；强调这是决策模型和人工验证。"
  },
  {
    channel: "X / Twitter",
    decision: "立即执行",
    role: "Build in public",
    cadence: "每天 1 条建设记录 + 10 条相关回复",
    url: "https://tools.kakijio.com/pricing-lab-tools/?utm_source=x&utm_medium=social&utm_campaign=pricing_lab_launch",
    guardrail: "只参与 SaaS、indie hacker、pricing 语境，避免泛流量。"
  },
  {
    channel: "Hacker News",
    decision: "延后到产品更强",
    role: "High-signal spike",
    cadence: "有更快、更独特的版本后再做 Show HN",
    url: "https://tools.kakijio.com/pricing-lab-tools/?utm_source=hackernews&utm_medium=community&utm_campaign=pricing_lab_launch",
    guardrail: "不在无关评论里插链接；只做一次真诚的 Show HN 或相关讨论回复。"
  },
  {
    channel: "Product Hunt",
    decision: "延后到 2-3 个工具",
    role: "Launch event",
    cadence: "等工具集更完整后集中发布",
    url: "https://tools.kakijio.com/pricing-lab-tools/?utm_source=producthunt&utm_medium=launch&utm_campaign=pricing_lab_launch",
    guardrail: "当前单页工具不值得消耗一次发布窗口。"
  }
];

const growthTasks = [
  "先用 tools.kakijio.com/pricing-lab-tools/ 作为唯一主链接，所有外部链接加 UTM。",
  "每天只做 15 条以内人工评论，优先回答真实问题，再自然给工具链接。",
  "把每条评论的原帖 URL、发布时间、UTM、回复数和线索数记录到后台任务或表格。",
  "连续 100 个合格访问没有 10 个表单线索，先改定位和页面，不继续铺量。"
];

const commentTemplates = [
  {
    title: "反馈请求",
    body: "I built a small SaaS pricing calculator to sanity-check cost floor, margin, competitor range, churn and CAC payback before changing a pricing page. It is not meant to replace customer interviews. If you are pricing a SaaS right now, I would appreciate blunt feedback."
  },
  {
    title: "讨论回复",
    body: "One mistake I see in early SaaS pricing is treating price as a single number. I would model cost floor, gross margin target, competitor corridor and CAC payback together, then test the range in customer calls."
  },
  {
    title: "无链接贡献",
    body: "Before copying a competitor's $19/$49/$99 tiers, I would check whether those prices support the founder's own support cost, churn assumption and acquisition payback. A price can look normal and still break the business."
  }
];

const cloudflareLimits = [
  ["Pages", "托管管理台和英文公开站点", "免费层每站 20,000 个文件"],
  ["Workers", "API、定时任务、健康检查", "免费层每日 100,000 次请求"],
  ["D1", "管理平台关系型数据库", "免费层 10 个库，单库 500MB，账号总 5GB"],
  ["KV", "配置、功能开关、轻量缓存", "每日 100k reads、1k writes，1GB 存储"],
  ["R2", "截图、导出文件、备份资料", "私有 siteops-assets bucket 已接入"],
  ["Turnstile", "保护表单，降低 bot 风险", "免费层适合大多数早期生产应用"],
  ["AI Gateway", "AI 请求日志、路由和成本控制", "免费层所有 gateway 合计 100k logs"]
];

const statusLabels: Record<string, string> = {
  shortlisted: "已入围",
  research: "调研中",
  draft: "草稿",
  planned: "待建设",
  live: "已上线",
  todo: "待办",
  doing: "进行中",
  blocked: "阻塞",
  done: "完成",
  high: "高",
  medium: "中",
  low: "低",
  calculator: "计算器",
  generator: "生成器",
  guide: "攻略/指南",
  tool: "工具"
};

function zh(label: string) {
  return statusLabels[label] ?? label;
}

function isR2NotEnabled(resource: CloudflareEndpoint) {
  return (
    resource.id === "r2-buckets" &&
    resource.status === 403 &&
    Boolean(resource.error?.includes("尚未启用 R2"))
  );
}

function resourceStatusLabel(resource: CloudflareEndpoint) {
  if (resource.id === "r2-binding" && resource.ok) return "已绑定";
  if (resource.ok) return "已连接";
  if (isR2NotEnabled(resource)) return "R2 未启用";
  return resource.enabled ? "接口异常" : "待配置";
}

export function App() {
  if (window.location.pathname.startsWith("/pricing-lab-tools")) {
    return <PricingLabPage />;
  }

  if (window.location.pathname.startsWith("/ai-video-factory")) {
    return <VideoFactoryPage />;
  }

  if (window.location.pathname.startsWith("/hot-guides")) {
    return <HotGuidesPage />;
  }

  const [active, setActive] = useState("dashboard");
  const [data, setData] = useState<DashboardData>(fallbackDashboard);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("正在加载运营数据...");
  const [cloudflare, setCloudflare] = useState<CloudflareOverview | null>(null);
  const [cloudflareLoading, setCloudflareLoading] = useState(false);
  const [videoGrowth, setVideoGrowth] = useState<VideoGrowthOverview>(fallbackVideoGrowth);
  const [videoGrowthLoading, setVideoGrowthLoading] = useState(false);
  const [growth, setGrowth] = useState<GrowthOverview>(fallbackGrowthOverview);
  const [growthLoading, setGrowthLoading] = useState(false);
  const [hotGuidesAutomation, setHotGuidesAutomation] = useState<HotGuidesAutomationOverview | null>(null);
  const [hotGuidesLoading, setHotGuidesLoading] = useState(false);

  useEffect(() => {
    void loadDashboard().then((next) => {
      setData(next);
      setLoading(false);
      setNotice("已连接 Cloudflare-first 运营模型。");
    });
  }, []);

  const rankedOpportunities = useMemo(
    () => [...data.opportunities].sort((a, b) => b.score - a.score),
    [data.opportunities]
  );

  async function refreshCloudflareOverview() {
    setCloudflareLoading(true);

    try {
      const overview = await loadCloudflareOverview();
      setCloudflare(overview);
      setNotice("已同步 Cloudflare API 状态。");
    } catch {
      setNotice("Cloudflare API 状态读取失败，请检查后台鉴权或 API Token。");
    } finally {
      setCloudflareLoading(false);
    }
  }

  useEffect(() => {
    if (active === "cloudflareApi" && !cloudflare && !cloudflareLoading) {
      void refreshCloudflareOverview();
    }
  }, [active, cloudflare, cloudflareLoading]);

  useEffect(() => {
    if (active !== "videoGrowth") return;

    setVideoGrowthLoading(true);
    void loadVideoGrowth()
      .then((next) => {
        setVideoGrowth(next);
        setNotice("已加载 AI Video Factory 增长漏斗。");
      })
      .catch(() => {
        setVideoGrowth(fallbackVideoGrowth);
        setNotice("视频增长数据读取失败，已显示本地占位。");
      })
      .finally(() => setVideoGrowthLoading(false));
  }, [active]);

  useEffect(() => {
    if (active !== "growth") return;

    setGrowthLoading(true);
    void loadGrowthOverview()
      .then((next) => {
        setGrowth(next);
        setNotice("已加载英文引流周报。");
      })
      .catch(() => {
        setGrowth(fallbackGrowthOverview);
        setNotice("增长周报读取失败，已显示本地占位。");
      })
      .finally(() => setGrowthLoading(false));
  }, [active]);

  useEffect(() => {
    if (active !== "hotGuidesAutomation") return;

    setHotGuidesLoading(true);
    void loadHotGuidesAutomation()
      .then((next) => {
        setHotGuidesAutomation(next);
        setNotice("已加载 Hot Guides 自动化状态。");
      })
      .catch(() => {
        setNotice("Hot Guides 自动化状态读取失败，请检查后台鉴权或 D1。");
      })
      .finally(() => setHotGuidesLoading(false));
  }, [active]);

  async function runAutomation(action: string) {
    setCloudflareLoading(true);

    try {
      const result = await runCloudflareAutomation(action);
      setNotice(result.message);
      const [overview, dashboard] = await Promise.all([loadCloudflareOverview(), loadDashboard()]);
      setCloudflare(overview);
      setData(dashboard);
    } catch {
      setNotice("自动化动作执行失败。");
    } finally {
      setCloudflareLoading(false);
    }
  }

  async function addOpportunity(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const payload = Object.fromEntries(form.entries()) as Partial<Opportunity>;
    payload.score = Number(payload.score ?? 24);

    try {
      const saved = await createOpportunity(payload);
      setData((current) => ({
        ...current,
        opportunities: [saved, ...current.opportunities]
      }));
      setNotice("机会已保存到 D1。");
      event.currentTarget.reset();
    } catch {
      const local: Opportunity = {
        id: `local_${Date.now()}`,
        title: String(payload.title ?? "Untitled opportunity"),
        niche: String(payload.niche ?? "unknown"),
        language: "en",
        primary_keyword: String(payload.primary_keyword ?? ""),
        audience: String(payload.audience ?? ""),
        problem: String(payload.problem ?? ""),
        monetization: String(payload.monetization ?? ""),
        score: Number(payload.score ?? 24),
        status: "draft",
        notes: "本地草稿；接入 D1 后可持久化。"
      };
      setData((current) => ({
        ...current,
        opportunities: [local, ...current.opportunities]
      }));
      setNotice("API 尚未连接，已保存为本地草稿。");
      event.currentTarget.reset();
    }
  }

  async function addSite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const payload = Object.fromEntries(form.entries()) as Partial<Site>;

    try {
      const saved = await createSite(payload);
      setData((current) => ({ ...current, sites: [saved, ...current.sites] }));
      setNotice("站点已保存到 D1。");
      event.currentTarget.reset();
    } catch {
      const local: Site = {
        id: `local_${Date.now()}`,
        name: String(payload.name ?? "Untitled site"),
        slug: String(payload.slug ?? "untitled-site"),
        domain: String(payload.domain ?? "") || null,
        type: String(payload.type ?? "tool"),
        status: "planned",
        niche: String(payload.niche ?? "unknown"),
        primary_keyword: String(payload.primary_keyword ?? ""),
        monetization: String(payload.monetization ?? "")
      };
      setData((current) => ({ ...current, sites: [local, ...current.sites] }));
      setNotice("API 尚未连接，已保存为本地草稿。");
      event.currentTarget.reset();
    }
  }

  async function addTask(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const payload = Object.fromEntries(form.entries()) as Partial<Task>;

    try {
      const saved = await createTask(payload);
      setData((current) => ({ ...current, tasks: [saved, ...current.tasks] }));
      setNotice("任务已保存到 D1。");
      event.currentTarget.reset();
    } catch {
      const local: Task = {
        id: `local_${Date.now()}`,
        title: String(payload.title ?? "未命名任务"),
        area: String(payload.area ?? "运营"),
        priority: String(payload.priority ?? "medium") as Task["priority"],
        status: "todo",
        notes: "本地草稿；接入 D1 后可持久化。"
      };
      setData((current) => ({ ...current, tasks: [local, ...current.tasks] }));
      setNotice("API 尚未连接，已保存为本地草稿。");
      event.currentTarget.reset();
    }
  }

  async function addGrowthAction(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const payload = Object.fromEntries(form.entries()) as Partial<GrowthAction>;
    payload.visits = Number(payload.visits ?? 0);
    payload.leads = Number(payload.leads ?? 0);

    try {
      await createGrowthAction(payload);
      const next = await loadGrowthOverview();
      setGrowth(next);
      setNotice("增长动作已保存到 D1。");
      event.currentTarget.reset();
    } catch {
      setNotice("增长动作保存失败，请检查必填字段和后台 API。");
    }
  }

  return (
    <main className="shell">
      <aside className="sidebar" aria-label="主导航">
        <div className="brand">
          <div className="brand-mark">AS</div>
          <div>
            <strong>AI 站点运营台</strong>
            <span>Cloudflare-first</span>
          </div>
        </div>

        <nav className="nav-list">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                className={active === item.id ? "nav-item active" : "nav-item"}
                key={item.id}
                onClick={() => setActive(item.id)}
                type="button"
              >
                <Icon size={18} />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="operator-note">
          <ShieldCheck size={18} />
          <p>
            管理系统使用中文；公开站点、内容、SEO、广告与转化素材使用英文。
          </p>
        </div>
      </aside>

      <section className="workspace">
        <header className="topbar">
          <div>
            <p className="eyebrow">英文市场一人公司运营系统</p>
            <h1>AI 英文站点资产管理台</h1>
          </div>
          <div className="status-pill">
            <Activity size={16} />
            {loading ? "加载中" : notice}
          </div>
        </header>

        {active === "dashboard" && (
          <section className="panel-stack">
            <div className="metrics-grid">
              {data.metrics.map((metric) => (
                <article className="metric" key={metric.label}>
                  <span>{metric.label}</span>
                  <strong>{metric.value}</strong>
                  <p>{metric.detail}</p>
                </article>
              ))}
            </div>

            <section className="split">
              <div className="surface">
                <SectionHeader
                  icon={Target}
                  title="优先机会"
                  action="先评分，再建设"
                />
                <div className="table">
                  {rankedOpportunities.slice(0, 5).map((opportunity) => (
                    <div className="row" key={opportunity.id}>
                      <div>
                        <strong>{opportunity.title}</strong>
                        <span>{opportunity.primary_keyword}</span>
                      </div>
                      <Badge label={`${opportunity.score}`} tone="strong" />
                      <Badge label={zh(opportunity.status)} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="surface">
                <SectionHeader icon={Rocket} title="本周重点" action="操盘循环" />
                <ul className="focus-list">
                  {data.weeklyFocus.map((item) => (
                    <li key={item}>
                      <CheckCircle2 size={18} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          </section>
        )}

        {active === "opportunities" && (
          <section className="panel-stack">
            <div className="surface">
              <SectionHeader icon={Search} title="机会录入" action="资产内容使用英文" />
              <form className="form-grid" onSubmit={addOpportunity}>
                <input name="title" placeholder="英文机会标题，如 SaaS Pricing Calculator" required />
                <input name="niche" placeholder="英文细分赛道，如 saas-operators" required />
                <input name="primary_keyword" placeholder="英文主关键词" required />
                <input name="audience" placeholder="英文目标用户描述" required />
                <input name="monetization" placeholder="英文变现路径/广告/offer" required />
                <input name="score" min="0" max="35" placeholder="评分 /35" type="number" />
                <textarea name="problem" placeholder="英文痛点证据或用户需求" required />
                <button type="submit">
                  <Plus size={16} />
                  添加机会
                </button>
              </form>
            </div>

            <OpportunityList opportunities={rankedOpportunities} />
          </section>
        )}

        {active === "sites" && (
          <section className="panel-stack">
            <div className="surface">
              <SectionHeader icon={Globe2} title="站点登记" action="Cloudflare Pages" />
              <form className="form-grid" onSubmit={addSite}>
                <input name="name" placeholder="英文站点名" required />
                <input name="slug" placeholder="英文 URL slug，如 pricing-lab-tools" required />
                <input name="domain" placeholder="英文域名，可选" />
                <input name="type" placeholder="英文类型：calculator / generator / guide" required />
                <input name="niche" placeholder="英文细分赛道" required />
                <input name="primary_keyword" placeholder="英文主关键词" required />
                <textarea name="monetization" placeholder="英文变现计划、广告位或 offer" required />
                <button type="submit">
                  <Plus size={16} />
                  添加站点
                </button>
              </form>
            </div>
            <SiteList sites={data.sites} />
          </section>
        )}

        {active === "factory" && (
          <section className="panel-stack">
            <div className="surface">
              <SectionHeader icon={Bot} title="页面工厂流程" action="人工审核门禁" />
              <div className="workflow-grid">
                {[
                  ["1", "关键词证据", "收集英文 SERP、Reddit、论坛和竞品页面。"],
                  ["2", "页面 Brief", "定义用户意图、目标角度、英文 CTA 和禁区。"],
                  ["3", "AI 英文草稿", "生成英文结构、文案和静态页面组件。"],
                  ["4", "人工审核", "检查事实、UX、合规、重复价值和广告承诺。"],
                  ["5", "部署上线", "通过 Pages 发布并记录部署元数据。"],
                  ["6", "数据复盘", "用访问、转化和收入信号决定优化或砍掉。"]
                ].map(([step, title, body]) => (
                  <article className="workflow-step" key={step}>
                    <span>{step}</span>
                    <strong>{title}</strong>
                    <p>{body}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {active === "growth" && (
          <section className="panel-stack">
            <div className="surface">
              <SectionHeader
                icon={Megaphone}
                title="英文引流周报"
                action={growthLoading ? "同步中" : `更新 ${formatTime(growth.generatedAt)}`}
              />
              <div className="metrics-grid">
                {growth.metrics.map((metric) => (
                  <article className="metric" key={metric.label}>
                    <span>{metric.label}</span>
                    <strong>{metric.value}</strong>
                    <p>{metric.detail}</p>
                  </article>
                ))}
              </div>
              <p className="decision-note">{growth.stopLine}</p>
            </div>

            <section className="split">
              <div className="surface">
                <SectionHeader icon={Plus} title="记录英文外部动作" action="人工审核后发布" />
                <form className="form-grid" onSubmit={addGrowthAction}>
                  <select name="site_id" defaultValue="site_pricing_lab">
                    <option value="site_pricing_lab">Pricing Lab Tools</option>
                    <option value="site_ai_video_factory">AI Video Factory</option>
                  </select>
                  <input name="channel" placeholder="渠道，如 Indie Hackers / Reddit / LinkedIn" required />
                  <select name="action_type" defaultValue="comment">
                    <option value="comment">评论回复</option>
                    <option value="post">独立发布</option>
                    <option value="dm">私信跟进</option>
                    <option value="directory">目录提交</option>
                  </select>
                  <select name="status" defaultValue="planned">
                    <option value="planned">计划</option>
                    <option value="published">已发布</option>
                    <option value="observing">观察中</option>
                    <option value="won">有效</option>
                    <option value="lost">无效</option>
                  </select>
                  <input
                    name="target_url"
                    placeholder="带 UTM 的目标链接"
                    required
                    type="url"
                  />
                  <input name="post_url" placeholder="原帖/评论 URL，可选" type="url" />
                  <input name="utm_source" placeholder="utm_source，如 indiehackers" required />
                  <input name="utm_medium" placeholder="utm_medium，如 community" defaultValue="community" />
                  <input name="utm_campaign" placeholder="utm_campaign" defaultValue="pricing_lab_launch" />
                  <input name="published_at" type="datetime-local" />
                  <input name="visits" min="0" placeholder="访问数" type="number" />
                  <input name="leads" min="0" placeholder="线索数" type="number" />
                  <textarea
                    name="message_angle"
                    placeholder="英文留言角度：你解决了什么问题、为什么对当前讨论有价值"
                    required
                  />
                  <textarea name="notes" placeholder="中文复盘：上下文、反馈、下一步动作" />
                  <button type="submit">
                    <Plus size={16} />
                    保存增长动作
                  </button>
                </form>
              </div>

              <div className="surface">
                <SectionHeader icon={BarChart3} title="渠道表现" action="按线索和访问排序" />
                <div className="source-list">
                  {growth.channels.length === 0 && <p className="empty-note">暂无渠道记录。</p>}
                  {growth.channels.map((item) => (
                    <article key={`${item.channel}-${item.source}`}>
                      <strong>{item.channel}</strong>
                      <span>{item.visits} 访问</span>
                      <span>{item.leads} 线索</span>
                    </article>
                  ))}
                </div>
              </div>
            </section>

            <section className="split">
              <div className="surface">
                <SectionHeader icon={Megaphone} title="英文流量渠道决策" action="价值优先，不群发" />
                <div className="growth-grid">
                  {growthChannels.map((item) => (
                    <article className="growth-card" key={item.channel}>
                      <div className="record-top">
                        <Badge label={item.decision} tone={item.decision === "立即执行" ? "strong" : undefined} />
                        <Badge label={item.role} />
                      </div>
                      <h3>{item.channel}</h3>
                      <p>{item.cadence}</p>
                      <a href={item.url} rel="noreferrer" target="_blank">
                        打开带 UTM 的英文链接
                      </a>
                      <span>{item.guardrail}</span>
                    </article>
                  ))}
                </div>
              </div>

              <div className="surface">
                <SectionHeader icon={MousePointerClick} title="最近增长动作" action="D1 记录" />
                <div className="lead-list">
                  {growth.actions.length === 0 && <p className="empty-note">暂无增长动作。</p>}
                  {growth.actions.map((action) => (
                    <article key={action.id}>
                      <div className="record-top">
                        <Badge label={action.channel} tone={action.status === "won" ? "strong" : undefined} />
                        <Badge label={action.status} />
                      </div>
                      <h3>{action.action_type}</h3>
                      <p>{action.message_angle}</p>
                      <span>
                        {action.utm_source} / {action.visits} visits / {action.leads} leads
                      </span>
                      {action.post_url && (
                        <a href={action.post_url} rel="noreferrer" target="_blank">
                          打开原帖
                        </a>
                      )}
                    </article>
                  ))}
                </div>
              </div>
            </section>

            <div className="surface">
              <SectionHeader icon={MessageSquareText} title="今日执行边界" action="人工发布" />
              <ul className="focus-list">
                {growthTasks.map((item) => (
                  <li key={item}>
                    <CheckCircle2 size={18} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="surface">
              <SectionHeader icon={MessageSquareText} title="英文评论素材" action="发布前按上下文改写" />
              <div className="template-grid">
                {commentTemplates.map((template) => (
                  <article className="template-card" key={template.title}>
                    <strong>{template.title}</strong>
                    <p>{template.body}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {active === "videoGrowth" && (
          <VideoGrowthPanel loading={videoGrowthLoading} overview={videoGrowth} />
        )}

        {active === "hotGuidesAutomation" && (
          <HotGuidesAutomationPanel loading={hotGuidesLoading} overview={hotGuidesAutomation} />
        )}

        {active === "tasks" && (
          <section className="panel-stack">
            <div className="surface">
              <SectionHeader icon={ListChecks} title="执行任务" action="90 天建设" />
              <form className="form-inline" onSubmit={addTask}>
                <input name="title" placeholder="中文任务标题" required />
                <input name="area" placeholder="任务模块，如 基础设施/选题/增长" required />
                <select name="priority" defaultValue="medium">
                  <option value="high">高</option>
                  <option value="medium">中</option>
                  <option value="low">低</option>
                </select>
                <button type="submit">
                  <Plus size={16} />
                  添加任务
                </button>
              </form>
              <TaskList tasks={data.tasks} />
            </div>
          </section>
        )}

        {active === "cloudflareApi" && (
          <section className="panel-stack">
            <div className="surface">
              <SectionHeader
                icon={Cloud}
                title="Cloudflare API 映射"
                action={cloudflareLoading ? "同步中" : "后台自动化"}
              />
              <div className="api-summary">
                <article>
                  <span>后台域名</span>
                  <strong>{cloudflare?.adminDomain ?? "admin-db.kakijio.com"}</strong>
                  <p>中文管理、登录、D1 数据和内部 API 入口。</p>
                </article>
                <article>
                  <span>公开工具域名</span>
                  <strong>{cloudflare?.publicToolsDomain ?? "tools.kakijio.com"}</strong>
                  <p>英文内容、SEO、广告和 lead form 入口。</p>
                </article>
                <article>
                  <span>API Token</span>
                  <strong>{cloudflare?.apiTokenConfigured ? "已配置" : "待配置"}</strong>
                  <p>配置后可实时读取 Cloudflare 资源；不把 token 写入仓库。</p>
                </article>
              </div>

              <button
                className="secondary-button"
                disabled={cloudflareLoading}
                onClick={refreshCloudflareOverview}
                type="button"
              >
                刷新 Cloudflare 状态
              </button>
            </div>

            <section className="split">
              <div className="surface">
                <SectionHeader icon={Database} title="资源接口状态" action="Cloudflare REST API" />
                <div className="resource-grid">
                  {(cloudflare?.resources ?? []).map((resource) => (
                    <article className="resource-card" key={resource.id}>
                      <div className="record-top">
                        <Badge
                          label={resourceStatusLabel(resource)}
                          tone={resource.ok ? "strong" : undefined}
                        />
                        <Badge label={resource.method} />
                      </div>
                      <h3>{resource.label}</h3>
                      <p>{resource.path ?? "缺少 account/zone 配置"}</p>
                      <dl>
                        <div>
                          <dt>数量</dt>
                          <dd>{resource.count ?? "-"}</dd>
                        </div>
                        <div>
                          <dt>状态</dt>
                          <dd>{resource.status ?? "未请求"}</dd>
                        </div>
                        <div>
                          <dt>权限</dt>
                          <dd>{resource.requires.join(" + ")}</dd>
                        </div>
                      </dl>
                      {resource.error && <span className="resource-error">{resource.error}</span>}
                    </article>
                  ))}
                </div>
              </div>

              <div className="surface">
                <SectionHeader icon={Workflow} title="自动化工具" action="受控执行" />
                <div className="automation-list">
                  {(cloudflare?.automations ?? []).map((automation) => (
                    <article className="automation-item" key={automation.action}>
                      <div>
                        <strong>{automation.label}</strong>
                        <p>{automation.description}</p>
                        <span>{automation.mode}</span>
                      </div>
                      <button
                        disabled={!automation.enabled || cloudflareLoading}
                        onClick={() => void runAutomation(automation.action)}
                        type="button"
                      >
                        执行
                      </button>
                    </article>
                  ))}
                </div>
              </div>
            </section>
          </section>
        )}

        {active === "stack" && (
          <section className="panel-stack">
            <div className="surface">
              <SectionHeader icon={Cloud} title="Cloudflare 免费资源栈" action="成本纪律" />
              <div className="stack-grid">
                {cloudflareLimits.map(([name, use, limit]) => (
                  <article className="stack-item" key={name}>
                    <Database size={18} />
                    <strong>{name}</strong>
                    <p>{use}</p>
                    <span>{limit}</span>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}
      </section>
    </main>
  );
}

function formatTime(value: string) {
  if (!value || value === "-") return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("zh-CN", {
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    month: "2-digit"
  });
}

function eventLabel(value: string) {
  const labels: Record<string, string> = {
    video_cta_click: "CTA 点击",
    video_generate_error: "生成失败",
    video_generate_submit: "提交生成",
    video_generate_success: "生成成功",
    video_lead_error: "线索失败",
    video_lead_submit: "提交线索",
    video_lead_success: "线索成功",
    video_packet_copy: "复制包",
    video_packet_download: "下载包",
    video_page_view: "页面浏览"
  };

  return labels[value] ?? value;
}

function VideoGrowthPanel({
  loading,
  overview
}: {
  loading: boolean;
  overview: VideoGrowthOverview;
}) {
  return (
    <section className="panel-stack">
      <div className="surface">
        <SectionHeader
          icon={Film}
          title="AI Video Factory 增长看板"
          action={loading ? "同步中" : `更新 ${formatTime(overview.generatedAt)}`}
        />
        <div className="metrics-grid">
          {overview.metrics.map((metric) => (
            <article className="metric" key={metric.label}>
              <span>{metric.label}</span>
              <strong>{metric.value}</strong>
              <p>{metric.detail}</p>
            </article>
          ))}
        </div>
      </div>

      <section className="split">
        <div className="surface">
          <SectionHeader icon={MousePointerClick} title="转化漏斗" action="视觉顺序事件" />
          <div className="funnel-list">
            {overview.funnel.map((item) => (
              <article key={item.event_name}>
                <div>
                  <strong>{eventLabel(item.event_name)}</strong>
                  <span>{item.event_name}</span>
                </div>
                <strong>{item.count}</strong>
                <span>{formatTime(item.last_seen)}</span>
              </article>
            ))}
          </div>
        </div>

        <div className="surface">
          <SectionHeader icon={Megaphone} title="来源质量" action="UTM 复盘" />
          <div className="source-list">
            {overview.sources.map((item) => (
              <article key={item.source}>
                <strong>{item.source}</strong>
                <span>{item.events} 事件</span>
                <span>{item.leads} 线索</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="split">
        <div className="surface">
          <SectionHeader icon={Mail} title="最近视频线索" action="人工跟进" />
          <div className="lead-list">
            {overview.recentLeads.length === 0 && <p className="empty-note">暂无视频线索。</p>}
            {overview.recentLeads.map((lead) => (
              <article key={lead.id}>
                <div className="record-top">
                  <Badge label={lead.urgency} tone={lead.urgency === "this week" ? "strong" : undefined} />
                  <Badge label={lead.turnstile_status} />
                </div>
                <h3>{lead.email}</h3>
                <p>{lead.use_case}</p>
                <span>
                  {lead.role} / {lead.utm_source || "direct"} / {formatTime(lead.created_at)}
                </span>
              </article>
            ))}
          </div>
        </div>

        <div className="surface">
          <SectionHeader icon={Wand2} title="最近生成" action="需求样本" />
          <div className="lead-list">
            {overview.recentProjects.length === 0 && <p className="empty-note">暂无生成记录。</p>}
            {overview.recentProjects.map((project) => (
              <article key={project.id}>
                <div className="record-top">
                  <Badge label={project.platform} />
                  <Badge label={project.engine} tone={project.engine === "workers-ai" ? "strong" : undefined} />
                </div>
                <h3>{project.topic}</h3>
                <p>{project.audience}</p>
                <span>{formatTime(project.created_at)}</span>
              </article>
            ))}
          </div>
        </div>
      </section>
    </section>
  );
}

function HotGuidesAutomationPanel({
  loading,
  overview
}: {
  loading: boolean;
  overview: HotGuidesAutomationOverview | null;
}) {
  const metrics = overview?.metrics ?? [
    { detail: "等待后台 API 返回", label: "活跃页面", value: "-" },
    { detail: "等待后台 API 返回", label: "需处理", value: "-" },
    { detail: "等待后台 API 返回", label: "搜索点击", value: "-" },
    { detail: "等待后台 API 返回", label: "站内行为率", value: "-" }
  ];

  return (
    <section className="panel-stack">
      <div className="surface">
        <SectionHeader
          icon={RefreshCw}
          title="Hot Guides 自动化"
          action={loading ? "同步中" : overview ? `更新 ${formatTime(overview.generatedAt)}` : "待连接"}
        />
        <div className="metrics-grid">
          {metrics.map((metric) => (
            <article className="metric" key={metric.label}>
              <span>{metric.label}</span>
              <strong>{metric.value}</strong>
              <p>{metric.detail}</p>
            </article>
          ))}
        </div>
        {overview && <p className="decision-note">{overview.stopLine}</p>}
        {overview && <p className="empty-note">{overview.gscNote}</p>}
      </div>

      <section className="split">
        <div className="surface">
          <SectionHeader icon={Target} title="页面决策" action="100 访问止损线" />
          <div className="lead-list">
            {!overview?.pages.length && <p className="empty-note">暂无自动化页面状态。</p>}
            {overview?.pages.map((page) => (
              <article key={page.id}>
                <div className="record-top">
                  <Badge
                    label={page.last_decision.replaceAll("_", " ")}
                    tone={
                      page.last_decision === "rewrite_title_hero" ||
                      page.last_decision === "source_attention"
                        ? "strong"
                        : undefined
                    }
                  />
                  <Badge label={page.last_source_status.replaceAll("_", " ")} />
                </div>
                <h3>{page.title_override ?? page.title}</h3>
                <p>{page.next_action}</p>
                {page.hero_variant_reason && <p>{page.hero_variant_reason}</p>}
                <span>
                  {page.qualified_visits_30d} visits / {page.search_clicks_30d} search clicks /{" "}
                  {page.onsite_actions_30d} actions
                </span>
              </article>
            ))}
          </div>
        </div>

        <div className="surface">
          <SectionHeader icon={ShieldCheck} title="最近来源检查" action="不编造 code 状态" />
          <div className="source-list">
            {!overview?.checks.length && <p className="empty-note">暂无来源检查记录。</p>}
            {overview?.checks.slice(0, 8).map((check) => (
              <article key={`${check.page_id}-${check.source_label}-${check.checked_at}`}>
                <strong>{check.source_label}</strong>
                <span>{check.ok ? "ok" : "failed"}</span>
                <span>{check.status_code ?? "-"}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="surface">
        <SectionHeader icon={BarChart3} title="最近自动决策" action="Cron log" />
        <div className="funnel-list">
          {!overview?.decisions.length && <p className="empty-note">暂无自动决策记录。</p>}
          {overview?.decisions.slice(0, 8).map((decision) => (
            <article key={`${decision.page_id}-${decision.checked_at}`}>
              <div>
                <strong>{decision.decision.replaceAll("_", " ")}</strong>
                <span>{decision.reason}</span>
              </div>
              <strong>{decision.qualified_visits_30d}</strong>
              <span>{formatTime(decision.checked_at)}</span>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function SectionHeader({
  icon: Icon,
  title,
  action
}: {
  icon: typeof Activity;
  title: string;
  action: string;
}) {
  return (
    <div className="section-header">
      <div>
        <Icon size={20} />
        <h2>{title}</h2>
      </div>
      <span>{action}</span>
    </div>
  );
}

function Badge({ label, tone }: { label: string; tone?: "strong" }) {
  return <span className={tone === "strong" ? "badge strong" : "badge"}>{label}</span>;
}

function OpportunityList({ opportunities }: { opportunities: Opportunity[] }) {
  return (
    <div className="surface">
      <SectionHeader icon={Gauge} title="评分队列" action="只建设高分机会" />
      <div className="card-grid">
        {opportunities.map((item) => (
          <article className="record" key={item.id}>
            <div className="record-top">
              <Badge label={`${item.score}/35`} tone="strong" />
              <Badge label={zh(item.status)} />
            </div>
            <h3>{item.title}</h3>
            <p>{item.problem}</p>
            <dl>
              <div>
                <dt>关键词</dt>
                <dd>{item.primary_keyword}</dd>
              </div>
              <div>
                <dt>用户</dt>
                <dd>{item.audience}</dd>
              </div>
              <div>
                <dt>变现</dt>
                <dd>{item.monetization}</dd>
              </div>
            </dl>
          </article>
        ))}
      </div>
    </div>
  );
}

function SiteList({ sites }: { sites: Site[] }) {
  return (
    <div className="surface">
      <SectionHeader icon={Globe2} title="站点资产" action="MVP 组合" />
      <div className="card-grid">
        {sites.map((site) => (
          <article className="record" key={site.id}>
            <div className="record-top">
              <Badge label={zh(site.status)} tone={site.status === "live" ? "strong" : undefined} />
              <Badge label={zh(site.type)} />
            </div>
            <h3>{site.name}</h3>
            <p>{site.primary_keyword}</p>
            <dl>
              <div>
                <dt>域名</dt>
                <dd>{site.domain || "未连接"}</dd>
              </div>
              <div>
                <dt>细分赛道</dt>
                <dd>{site.niche}</dd>
              </div>
              <div>
                <dt>收入路径</dt>
                <dd>{site.monetization}</dd>
              </div>
            </dl>
          </article>
        ))}
      </div>
    </div>
  );
}

function TaskList({ tasks }: { tasks: Task[] }) {
  return (
    <div className="task-list">
      {tasks.map((task) => (
        <div className="task-row" key={task.id}>
          <div>
            <strong>{task.title}</strong>
            <span>{task.area}</span>
          </div>
          <Badge label={zh(task.priority)} tone={task.priority === "high" ? "strong" : undefined} />
          <Badge label={zh(task.status)} />
          {task.notes && <p>{task.notes}</p>}
        </div>
      ))}
    </div>
  );
}
